<template>
  <div class="filters-field-controls">
    <div
      v-if="!hideLabelRow"
      class="small text-muted mb-1 text-truncate"
      :title="$t(field.label)"
    >
      <i :class="[filterIconClass, 'me-1']"></i>{{ $t(field.label) }}
      <button
        v-if="filterActive"
        type="button"
        class="btn btn-link btn-sm text-danger p-0 float-end"
        @click="$emit('clear')"
      >
        <i class="fas fa-times"></i>
      </button>
    </div>

    <div
      v-if="
        [
          'string',
          'text',
          'email',
          'textarea',
          'varchar',
          'char',
          'autoincrement',
        ].includes(field.type)
      "
      class="string-filter"
    >
      <FieldText
        :field="field"
        :value="filterValue"
        mode="edit"
        :fieldId="'filter-' + field.key"
        :editingFields="field.key"
        :disableLock="true"
        :isFilter="true"
        :filterOperator="filterOperator || '='"
        @input="$emit('debounced-input', $event)"
        @filter-operator-change="$emit('operator-change', $event)"
      />
    </div>

    <div
      v-else-if="field.key === 'status' && modelName === 'shipping'"
      class="shipping-status-filter"
    >
      <div class="input-group input-group-sm">
        <select
          :value="filterOperator || '='"
          class="form-select form-select-sm filter-op-select"
          @change="$emit('operator-change', $event.target.value)"
        >
          <option value="=">Equal to</option>
          <option value="!=">Different from</option>
          <option value="is_null">Is empty</option>
          <option value="is_not_null">Is not empty</option>
        </select>
        <select
          class="form-select form-select-sm flex-grow-1"
          :value="
            filterValue != null && filterValue !== ''
              ? String(Number(filterValue))
              : ''
          "
          :disabled="
            filterOperator === 'is_null' || filterOperator === 'is_not_null'
          "
          @change="$emit('shipping-status-change', $event)"
        >
          <option value="">Select a status...</option>
          <option value="1">To be coordinated</option>
          <option value="2">Coordinated</option>
          <option value="3">Consolidated</option>
          <option value="4">On board</option>
          <option value="5">Upcoming ETA</option>
          <option value="6">Gate out</option>
        </select>
      </div>
    </div>

    <div v-else-if="field.type === 'number'" class="number-filter">
      <FieldNumber
        :field="field"
        :value="filterValue"
        mode="edit"
        :fieldId="'filter-' + field.key"
        :editingFields="field.key"
        :disableLock="true"
        :isFilter="true"
        :filterOperator="filterOperator || '='"
        @input="$emit('debounced-input', $event)"
        @filter-operator-change="$emit('operator-change', $event)"
      />
    </div>

    <div v-else-if="isDateField" class="date-filter">
      <div class="input-group input-group-sm flex-nowrap mb-1 date-filter-row">
        <select
          :value="filterOperator || '='"
          class="form-select form-select-sm filter-op-select"
          @change="$emit('date-operator-change', $event.target.value)"
        >
          <option
            v-for="option in dateOperatorOptions"
            :key="`date-op-${option.value}`"
            :value="option.value"
          >
            {{ option.label }}
          </option>
        </select>
        <input
          type="date"
          class="form-control form-control-sm flex-grow-1 min-w-0"
          :value="(tempDateRange && tempDateRange.start) || ''"
          :disabled="
            filterOperator === 'is_null' || filterOperator === 'is_not_null'
          "
          @input="
            $emit('date-range-input', {
              part: 'start',
              value: $event.target.value,
            })
          "
        />
        <input
          v-if="isDateBetweenOperator"
          type="date"
          class="form-control form-control-sm flex-grow-1 min-w-0"
          :value="(tempDateRange && tempDateRange.end) || ''"
          @input="
            $emit('date-range-input', {
              part: 'end',
              value: $event.target.value,
            })
          "
        />
      </div>
      <button
        type="button"
        class="btn btn-link btn-sm text-muted px-0 py-0 mt-1"
        @click="$emit('clear-date-range')"
      >
        <i class="fas fa-times me-1"></i>Clear dates
      </button>
    </div>

    <div v-else-if="field.type === 'textlist'" class="textlist-filter">
      <InputTextList
        :field="field"
        :value="filterValue"
        :modelName="modelName"
        :hideLabel="true"
        :showPreview="true"
        placeholder="Select an option"
        @input="$emit('immediate-input', $event)"
      />
    </div>

    <div v-else-if="field.type === 'list'" class="list-filter">
      <div class="input-group input-group-sm filter-input-with-multiselect">
        <select
          :value="filterOperator || '='"
          class="form-select form-select-sm filter-op-select"
          @change="$emit('operator-change', $event.target.value)"
        >
          <option value="=">Equal to</option>
          <option value="!=">Different from</option>
          <option value="is_null">Is empty</option>
          <option value="is_not_null">Is not empty</option>
        </select>
        <div class="filter-multiselect-wrap">
          <multiselect
            :value="normalizedListFilterValue"
            :options="normalizedListOptions"
            :multiple="true"
            :close-on-select="false"
            :clear-on-select="true"
            :preserve-search="true"
            :preselect-first="false"
            :allow-empty="true"
            :searchable="true"
            :show-labels="true"
            label="label"
            track-by="_id"
            placeholder="Select..."
            @input="$emit('immediate-input', $event)"
            @remove="$emit('immediate-input', null)"
            :custom-label="(o) => (o && o.$path) || multiselectListLabel(o)"
          >
            <template #option="{ option }">
              <FlagOptionLabel
                v-if="isFlagListField"
                :label="option.label || ''"
                :color="option.color || ''"
                :depth="option.$depth || 0"
              />
              <ListTreeOptionLabel
                v-else
                :label="multiselectListLabel(option)"
                :depth="option.$depth || 0"
                :has-children="option.$hasChildren"
              />
            </template>
          </multiselect>
        </div>
      </div>
    </div>

    <div v-else-if="field.type === 'reference'" class="reference-filter">
      <div class="input-group input-group-sm filter-input-with-multiselect">
        <select
          :value="filterOperator || '='"
          class="form-select form-select-sm filter-op-select"
          @change="$emit('operator-change', $event.target.value)"
        >
          <option value="=">Equal to</option>
          <option value="!=">Different from</option>
          <option value="is_null">Is empty</option>
          <option value="is_not_null">Is not empty</option>
        </select>
        <div class="filter-multiselect-wrap">
          <multiselect
            :value="filterValue"
            :options="referenceMultiselectOptions"
            :multiple="true"
            :close-on-select="false"
            :clear-on-select="true"
            :preserve-search="true"
            :preselect-first="false"
            :allow-empty="true"
            :searchable="true"
            :internal-search="isUsersReferenceField"
            :show-labels="true"
            label="name"
            track-by="_id"
            :placeholder="isUsersReferenceField ? 'Users...' : 'Search...'"
            :loading="
              isUsersReferenceField
                ? usersOptionsLoading && !(usersOptions || []).length
                : searchingReference
            "
            @input="$emit('immediate-input', $event)"
            @remove="$emit('immediate-input', null)"
            @search-change="onReferenceMultiselectSearch"
            :custom-label="referenceMultiselectLabel"
          />
        </div>
      </div>
    </div>

    <div v-else-if="field.type === 'boolean'" class="boolean-filter">
      <div class="input-group input-group-sm filter-boolean-row">
        <select
          :value="filterOperator || '='"
          class="form-select form-select-sm filter-op-select"
          @change="$emit('operator-change', $event.target.value)"
        >
          <option value="=">Equal to</option>
          <option value="!=">Different from</option>
          <option value="is_null">Is empty</option>
          <option value="is_not_null">Is not empty</option>
        </select>
        <span
          class="input-group-text p-0 flex-grow-1 bg-white border-start-0 filter-boolean-buttons"
        >
          <div class="d-flex w-100 h-100">
            <button
              type="button"
              class="btn btn-sm flex-fill rounded-0 border-0"
              :class="
                filterValue === true ? 'btn-primary' : 'btn-outline-secondary'
              "
              @click="$emit('immediate-input', true)"
            >
              Yes
            </button>
            <button
              type="button"
              class="btn btn-sm flex-fill rounded-0 border-0"
              :class="
                filterValue === false ? 'btn-primary' : 'btn-outline-secondary'
              "
              @click="$emit('immediate-input', false)"
            >
              No
            </button>
          </div>
        </span>
      </div>
    </div>

    <div
      v-else-if="
        field.type === 'reference_subfield' && field.subFieldRef === 'users'
      "
    >
      <div v-if="!hideLabelRow" class="small text-muted mb-1">
        {{ $t(field.parentField.label) }} → {{ field.subFieldKey }}
      </div>
      <div class="input-group input-group-sm filter-input-with-multiselect">
        <select
          :value="filterOperator || '='"
          class="form-select form-select-sm filter-op-select"
          @change="$emit('operator-change', $event.target.value)"
        >
          <option value="=">Equal to</option>
          <option value="!=">Different from</option>
          <option value="is_null">Is empty</option>
          <option value="is_not_null">Is not empty</option>
        </select>
        <div class="filter-multiselect-wrap">
          <multiselect
            :value="filterValue"
            :options="usersOptions || []"
            :multiple="true"
            :close-on-select="false"
            :clear-on-select="true"
            :preserve-search="true"
            :preselect-first="false"
            :allow-empty="true"
            :searchable="true"
            :show-labels="true"
            label="name"
            track-by="_id"
            placeholder="Users..."
            :loading="usersOptionsLoading && !(usersOptions || []).length"
            @input="$emit('immediate-input', $event)"
            @remove="$emit('immediate-input', null)"
            :custom-label="referenceMultiselectLabel"
          />
        </div>
      </div>
    </div>

    <div
      v-else-if="
        field.type === 'reference_subfield' && field.subFieldType === 'number'
      "
      class="number-filter"
    >
      <div class="input-group input-group-sm">
        <select
          :value="filterOperator || '='"
          class="form-select form-select-sm filter-op-select"
          @change="$emit('operator-change', $event.target.value)"
        >
          <option value="=">Equal</option>
          <option value=">">Greater than</option>
          <option value="<">Less than</option>
          <option value=">=">On or after</option>
          <option value="<=">On or before</option>
          <option value="!=">Not equal</option>
          <option value="is_null">Is empty</option>
          <option value="is_not_null">Is not empty</option>
        </select>
        <input
          type="number"
          :value="filterValue"
          class="form-control form-control-sm"
          @input="$emit('ref-subfield-number-input', $event.target.value)"
        />
      </div>
    </div>

    <div
      v-else-if="
        field.type === 'reference_subfield' && field.subFieldType === 'list'
      "
      class="list-filter"
    >
      <div v-if="!hideLabelRow" class="small text-muted mb-1">
        {{ $t(field.parentField.label) }} → {{ field.subFieldKey }}
      </div>
      <div class="input-group input-group-sm filter-input-with-multiselect">
        <select
          :value="filterOperator || '='"
          class="form-select form-select-sm filter-op-select"
          @change="$emit('operator-change', $event.target.value)"
        >
          <option value="=">Equal to</option>
          <option value="!=">Different from</option>
          <option value="is_null">Is empty</option>
          <option value="is_not_null">Is not empty</option>
        </select>
        <div class="filter-multiselect-wrap">
          <multiselect
            :value="normalizedListSubFilterValue"
            :options="normalizedListSubOptions"
            :multiple="true"
            :close-on-select="false"
            :clear-on-select="true"
            :preserve-search="true"
            :preselect-first="false"
            :allow-empty="true"
            :searchable="true"
            :show-labels="true"
            label="label"
            track-by="_id"
            placeholder="Select..."
            @input="$emit('immediate-input', $event)"
            @remove="$emit('immediate-input', null)"
            :custom-label="(o) => (o && o.$path) || multiselectListLabelSub(o)"
          >
            <template #option="{ option }">
              <ListTreeOptionLabel
                :label="multiselectListLabelSub(option)"
                :depth="option.$depth || 0"
                :has-children="option.$hasChildren"
              />
            </template>
          </multiselect>
        </div>
      </div>
    </div>

    <div v-else-if="field.type === 'reference_subfield'">
      <div v-if="!hideLabelRow" class="small text-muted mb-1">
        {{ $t(field.parentField.label) }} → {{ field.subFieldKey }}
      </div>
      <div class="input-group input-group-sm">
        <select
          :value="filterOperator || '='"
          class="form-select form-select-sm filter-op-select"
          @change="$emit('operator-change', $event.target.value)"
        >
          <option value="=">Equal to</option>
          <option value="regex">Contains</option>
          <option value="startswith">Starts with</option>
          <option value="endswith">Ends with</option>
          <option value="!=">Different from</option>
          <option value="is_null">Is empty</option>
          <option value="is_not_null">Is not empty</option>
        </select>
        <input
          type="text"
          :value="filterValue"
          class="form-control form-control-sm"
          @input="$emit('debounced-input', $event.target.value)"
        />
      </div>
    </div>
  </div>
</template>

<script>
import Multiselect from "vue-multiselect";
import "vue-multiselect/dist/vue-multiselect.min.css";
import { InputTextList } from "./common/inputs";
import { FieldText, FieldNumber } from "./model-definition-core";
import { i18n } from "@/i18n";
import FlagOptionLabel from "./common/FlagOptionLabel.vue";
import ListTreeOptionLabel from "./common/ListTreeOptionLabel.vue";
import { FLAG_FIELD_KEY } from "@/utils/financialMovementCategory";
import { listValuesInTreeOrder } from "@/utils/listValueTree";

const DATE_OPERATOR_OPTIONS = [
  { value: "=", label: "Equal to" },
  { value: ">", label: "After" },
  { value: "<", label: "Before" },
  { value: ">=", label: "On or after" },
  { value: "<=", label: "On or before" },
  { value: "between", label: "Between" },
  { value: "!=", label: "Different from" },
  { value: "is_null", label: "Is empty" },
  { value: "is_not_null", label: "Is not empty" },
];

export default {
  name: "FiltersFieldControls",
  components: {
    Multiselect,
    FlagOptionLabel,
    ListTreeOptionLabel,
    InputTextList,
    FieldText,
    FieldNumber,
  },
  props: {
    field: { type: Object, required: true },
    modelName: { type: String, default: "" },
    filterValue: {},
    filterOperator: { type: String, default: "=" },
    tempDateRange: {
      type: Object,
      default: () => ({ start: "", end: "" }),
    },
    listOptions: { type: Array, default: () => [] },
    referenceOptionsList: { type: Array, default: () => [] },
    usersOptions: { type: Array, default: () => [] },
    usersOptionsLoading: { type: Boolean, default: false },
    listOptionsSubField: { type: Array, default: () => [] },
    searchingReference: { type: Boolean, default: false },
    hideLabelRow: { type: Boolean, default: false },
    isDateField: { type: Boolean, default: false },
    filterActive: { type: Boolean, default: false },
    multiselectListLabel: { type: Function, required: true },
    multiselectListLabelSub: { type: Function, required: true },
    referenceMultiselectLabel: { type: Function, required: true },
  },
  computed: {
    /** Solo «Between» usa dos fechas; el resto de operadores usa una sola. */
    isDateBetweenOperator() {
      return this.filterOperator === "between";
    },
    /**
     * Un filtro puede declarar qué operadores tienen sentido
     * (`field.filterOperators`; p. ej. "Saldo al" no ofrece «vacío»).
     */
    dateOperatorOptions() {
      const allowed = Array.isArray(this.field.filterOperators)
        ? this.field.filterOperators
        : null;
      if (!allowed || !allowed.length) return DATE_OPERATOR_OPTIONS;
      return DATE_OPERATOR_OPTIONS.filter((option) =>
        allowed.includes(option.value)
      );
    },
    isUsersReferenceField() {
      return this.field.type === "reference" && this.field.ref === "users";
    },
    referenceMultiselectOptions() {
      if (this.isUsersReferenceField) {
        return this.usersOptions || [];
      }
      return this.referenceOptionsList || [];
    },
    /** Filtro de banderas: sus opciones van con la jerarquía y el color de la lista. */
    isFlagListField() {
      return this.field.type === "list" && this.field.key === FLAG_FIELD_KEY;
    },
    // Opciones en el orden del árbol de la lista: elegir un padre filtra también por
    // sus hijos (lo resuelve el back), y lo elegido se ve con su ruta («MANI > BAYO»)
    normalizedListOptions() {
      return this.listOptionsInTreeOrder(this.normalizeListOptions(this.listOptions));
    },
    normalizedListSubOptions() {
      return this.listOptionsInTreeOrder(this.normalizeListOptions(this.listOptionsSubField));
    },
    normalizedListFilterValue() {
      return this.normalizeListFilterValue(
        this.filterValue,
        this.normalizedListOptions
      );
    },
    normalizedListSubFilterValue() {
      return this.normalizeListFilterValue(
        this.filterValue,
        this.normalizedListSubOptions
      );
    },
    filterIconClass() {
      const icons = {
        string: "fas fa-font",
        number: "fas fa-hashtag",
        date: "fas fa-calendar",
        boolean: "fas fa-check-square",
        reference: "fas fa-link",
        reference_subfield: "fas fa-project-diagram",
        list: "fas fa-list",
        textlist: "fas fa-list-ul",
        group: "fas fa-folder",
        array: "fas fa-list-ul",
      };
      return icons[this.field.type] || "fas fa-filter text-primary";
    },
  },
  methods: {
    listOptionsInTreeOrder(options) {
      const tree = listValuesInTreeOrder(options);
      const paths = [];
      return tree.map(({ value, depth }, i) => {
        const label = value.label || value.name || String(value._id);
        paths[depth] = label;
        const next = tree[i + 1];
        return {
          ...value,
          $depth: depth,
          $hasChildren: !!next && next.depth > depth,
          $path: depth > 0 ? paths.slice(0, depth + 1).join(" > ") : null,
        };
      });
    },
    /** vue-multiselect track-by falla si _id es ObjectId vs string de la URL/reporte. */
    normalizeListOptions(options) {
      if (!Array.isArray(options)) return [];
      return options.map((opt) => {
        if (!opt || typeof opt !== "object") return opt;
        const id = opt._id != null ? String(opt._id) : opt._id;
        if (id === opt._id) return opt;
        return { ...opt, _id: id };
      });
    },
    /**
     * Convierte ids (string/URL/reporte) a objetos de opción con label.
     * multiple:true espera array; un id suelto se envuelve.
     */
    normalizeListFilterValue(value, options) {
      const opts = Array.isArray(options) ? options : [];
      const resolveOne = (raw) => {
        if (raw == null || raw === "") return null;
        if (typeof raw === "object" && raw._id != null) {
          const id = String(raw._id);
          const hit = opts.find((o) => o && String(o._id) === id);
          if (hit) return hit;
          return {
            ...raw,
            _id: id,
            label: raw.label || raw.name || id,
          };
        }
        const id = String(raw);
        const hit = opts.find((o) => o && String(o._id) === id);
        if (hit) return hit;
        return { _id: id, label: id };
      };

      if (Array.isArray(value)) {
        return value.map(resolveOne).filter(Boolean);
      }
      if (typeof value === "string" && value.includes(",")) {
        return value
          .split(",")
          .map((part) => part.trim())
          .filter(Boolean)
          .map(resolveOne)
          .filter(Boolean);
      }
      const one = resolveOne(value);
      return one ? [one] : [];
    },
    onReferenceMultiselectSearch(searchTerm) {
      if (this.isUsersReferenceField) return;
      this.$emit("reference-search", searchTerm);
    },
    $t(key) {
      return i18n.t(key);
    },
  },
};
</script>

<style scoped>
/*
 * Operador + multiselect (list/reference): debe vivir aquí — scoped del padre no aplica a estos nodos.
 * Bootstrap 5: .input-group > .form-select { flex: 1 1 auto; width: 1%; } iguala anchos con el multiselect.
 */
.filter-input-with-multiselect.input-group {
  display: flex !important;
  flex-wrap: nowrap !important;
  align-items: stretch;
  width: 100%;
}

.filter-input-with-multiselect > .filter-op-select.form-select,
.filter-input-with-multiselect > select.filter-op-select {
  flex: 0 0 auto !important;
  width: auto !important;
  max-width: 9.5rem !important;
  min-width: 5.75rem;
}

.filter-input-with-multiselect > .filter-multiselect-wrap {
  flex: 1 1 0 !important;
  min-width: 0 !important;
  display: flex !important;
  align-items: stretch;
}

.filter-multiselect-wrap :deep(.multiselect) {
  flex: 1 1 auto;
  width: 100% !important;
  min-width: 0;
  min-height: 31px;
  border-top-left-radius: 0;
  border-bottom-left-radius: 0;
}

.filter-multiselect-wrap :deep(.multiselect__tags) {
  min-height: 31px;
  padding: 0.2rem 2rem 0.2rem 0.45rem;
}

.date-filter-row {
  align-items: stretch;
}

.filter-boolean-row {
  align-items: stretch;
}

.filter-boolean-buttons {
  align-items: stretch;
}
</style>
